//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//
import axios from './axios';
import { useConfig } from '../Config';
import Q from 'q';
import async from 'async';
import { User } from 'oidc-client-ts';
import { ResponseType } from 'axios';
import { getSafeToken } from './securityUtils';
import {
  RegisterUserResponse,
  Teamspace,
  UserTeamspaces,
  Tenant,
  TeamspaceResponse,
  FetchKeycloakUserResponse,
  AddUserToTeamSpaceResponse,
  TeamspaceTaskResponse,
  AddUserToTeamspaceBody,
  FetchTeamspaceUserResponse,
  SaveIntegrationsResponse,
  GetTeamspaceIntegrationsResponse,
  AiPropagationStatusResponse,
  AllTeamspaceTasksResponse,
  TeamspaceProvisionData,
  TeamspaceProvisioningStatusResponse,
  TeamspaceUserUpdateTaskDto,
  AnnouncementDto,
  CreateAnnouncementRequest,
} from '../components/Sidebar/SidebarTypes';

export interface DiagnosticArchive {
  filename: string;
  sizeBytes: number;
  sizeDisplay: string;
  timestamp: string;
  uploaded: boolean;
}

/** Returned exactly once at mint time. `token` is never persisted server-side. */
export interface OfflineTokenResponse {
  grantId: string;
  token: string;
  boundaryType: 'INTERNAL' | 'EXTERNAL';
  rotating: boolean;
  fixedExpiresAt: string | null;
  mintedAt: string;
}

type JSONPrimitive = string | number | boolean;
type JSONObject = { [key: string]: JSONValue };
type JSONArray = JSONValue[];
type JSONValue = JSONPrimitive | JSONObject | JSONArray | undefined;
type InvokeHrefResp = Teamspace &
  TeamspaceTaskResponse &
  AllTeamspaceTasksResponse;
type RetryFailedTeamspaceTaskResp =
  | { status: number; data: string }
  | TeamspaceTaskResponse;
type UserTeamspaceRoleResp = { status: number } | string;

interface QueueOptions {
  method: string;
  url: string;
  body: object;
  headers: { [key: string]: string };
  queryParameters: { [key: string]: string };
  form: FormData;
  deferred: Q.Deferred<any>;
  responseType: ResponseType;
}

/**
 * Prepares parameters to be sent as part of the URL, they must be encoded as a UTF-8 string.
 * Parameters are joined together using '&' and a '=' separates parameter names from their values
 *
 * @param {object} parameters - contains the parameters and their values
 *
 * @returns a conversion of a list of parameters into a UTF-8 string that can be tacked onto the end of a URL sent in an HTTP request
 */
function serializeQueryParams(parameters: { [key: string]: string }) {
  const str = [];
  for (const p in parameters) {
    if (parameters.hasOwnProperty(p)) {
      str.push(encodeURIComponent(p) + '=' + encodeURIComponent(parameters[p]));
    }
  }
  return str.join('&');
}

class TenantDataService {
  static instance: TenantDataService;
  private readonly queueAPICalls: async.QueueObject<unknown>;
  private readonly tenantBaseUrl: string;
  static readonly saveIntegrations: any;
  constructor(getUserContext: () => User, getIsAuthenticated: () => boolean) {
    const { services } = useConfig();
    if (!TenantDataService.instance) {
      TenantDataService.instance = this;
    }
    this.tenantBaseUrl = services?.tenant?.url ?? '';

    this.queueAPICalls = async.queue((options: QueueOptions, callback) => {
      TenantDataService.instance._request(
        options.method,
        options.url,
        options.body,
        options.headers,
        options.queryParameters,
        options.form,
        options.deferred,
        function () {
          callback();
        },
        getUserContext,
        getIsAuthenticated,
        services?.tenant?.url,
        options.responseType,
      );
    }, 3);
  }

  /**
   * Returns the tenant host origin (base URL with the trailing `/rest`
   * segment removed). Some endpoints — such as the teamspace provisioning
   * status endpoint — live outside the `/rest` namespace, so callers must
   * construct an absolute URL using this origin instead of relying on path
   * traversal (`/../`) against the configured base URL.
   *
   * @returns The tenant host origin without the `/rest` suffix.
   */
  private getTenantOrigin(): string {
    return this.tenantBaseUrl.replace(/\/rest\/?$/, '');
  }

  /**
   * The main request method that all API method will eventually call.
   *
   * @param method - http method
   * @param url - url to do request
   * @param body - body parameters / object
   * @param headers - header parameters
   * @param queryParameters - querystring parameters
   * @param form - form data object
   * @param deferred - promise object
   * @param completeJob - Call back to queue to communicate that job is done and the queue can pick up new job.
   * @param getUserContext - Function that returns user object to get access token from
   * @param getIsAuthenticated - Function that returns boolean value determining if user is currently authenticated
   * @param baseUrl - Request base url
   * @param responseType - Expected type of the response to the HTTP request, defaults to JSON
   */
  public _request(
    method: string,
    url: string,
    body: object,
    headers: { [key: string]: string },
    queryParameters: { [key: string]: string },
    form: FormData | object,
    deferred: Q.Deferred<any>,
    completeJob: () => void,
    getUserContext: () => User,
    getIsAuthenticated: () => boolean,
    baseUrl: string | undefined,
    responseType: ResponseType = 'json',
  ) {
    const user = getUserContext();
    const isAuthenticated = getIsAuthenticated();
    const queryParams =
      queryParameters && Object.keys(queryParameters).length
        ? serializeQueryParams(queryParameters)
        : null;
    const urlWithParams = url + (queryParams ? '?' + queryParams : '');
    // if user is logged in
    headers['Authorization'] = 'Bearer ' + getSafeToken(user.access_token);
    if (!isAuthenticated) {
      deferred.reject('User is not logged in.');
      completeJob();
    }
    if (body && !Object.keys(body).length && !Array.isArray(body)) {
      body = {};
    }
    axios({
      method,
      url: urlWithParams,
      baseURL: baseUrl,
      headers,
      responseType: responseType,
      // Prefer `form` as payload if it's explicitly provided and not empty
      // otherwise use `body` for typical JSON payloads like in POST requests.
      // GET requests carry no body: passing an object (even `{}`) makes axios
      // auto-attach `Content-Type: application/json` plus an empty JSON body to
      // every read, so send `undefined` for GET to keep reads clean.
      data:
        method === 'POST'
          ? Object.keys(form).length
            ? form
            : body
          : method === 'GET'
            ? undefined
            : body,
    })
      .then((response) => {
        deferred.resolve(response.data || response);
        completeJob();
      })
      .catch((error) => {
        deferred.reject(error.response);
        completeJob();
      });
  }

  public request<T>(
    method: string,
    url: string,
    body: object,
    headers: JSONValue,
    queryParameters: JSONValue,
    form: JSONValue,
    deferred: Q.Deferred<T>,
    responseType = 'json',
  ) {
    this.queueAPICalls.push({
      method: method,
      url: url,
      body: body,
      headers: headers,
      queryParameters: queryParameters,
      form: form,
      deferred: deferred,
      responseType: responseType,
    });
  }

  getAllTenants() {
    const deferred = Q.defer();
    this.request('GET', '/tenants', {}, {}, {}, {}, deferred);
    return deferred.promise as Q.Promise<Tenant[]>;
  }

  registerUser() {
    const deferred = Q.defer();
    this.request('POST', '/users/registeruser', {}, {}, {}, {}, deferred);
    return deferred.promise as Q.Promise<RegisterUserResponse>;
  }

  createTenant(tenant: { name: string }) {
    const deferred = Q.defer();
    const body = { tenant };
    this.request('POST', '/tenants/registration', body, {}, {}, {}, deferred);
    return deferred.promise;
  }

  provisionTeamspace(data: TeamspaceProvisionData) {
    const deferred = Q.defer<Teamspace>();
    this.request('POST', '/teamspaces/provision', data, {}, {}, {}, deferred);
    return deferred.promise;
  }

  getTeamspaceProvisioningStatus(teamspaceId: string) {
    const deferred = Q.defer<TeamspaceProvisioningStatusResponse>();
    // This endpoint lives outside the `/rest` namespace per the API spec.
    // Build an absolute URL from the tenant origin so axios bypasses the
    // `baseURL` (which includes `/rest`) instead of relying on `/../`
    // path-traversal-style resolution. `teamspaceId` is URL-encoded
    // defensively even though callers pass server-generated IDs.
    const url = `${this.getTenantOrigin()}/teamspaces/${encodeURIComponent(
      teamspaceId,
    )}/provisioning-status`;
    this.request('GET', url, {}, {}, {}, {}, deferred);
    return deferred.promise;
  }
  createTeamspaceTask(task: {
    teamspace: string;
    type: string;
    tenant: string;
    name: string;
    async: boolean;
  }) {
    const deferred = Q.defer();
    const body = task;
    this.request('POST', '/teamspaces/tasks', body, {}, {}, {}, deferred);
    return deferred.promise as Q.Promise<TeamspaceTaskResponse>;
  }

  getUser(userId: string) {
    const deferred = Q.defer();
    const url = '/users/' + userId;
    this.request('GET', url, {}, {}, {}, {}, deferred);
    return deferred.promise;
  }

  getTenant(tenantID: string) {
    const deferred = Q.defer();
    const url = '/tenants/' + tenantID;
    this.request('GET', url, {}, {}, {}, {}, deferred);
    return deferred.promise;
  }

  getTeamspace(teamspaceId: string | undefined) {
    const deferred = Q.defer();
    const url = '/teamspaces/' + teamspaceId;
    this.request('GET', url, {}, {}, {}, {}, deferred);
    return deferred.promise as Q.Promise<Teamspace>;
  }

  /**
   * Lazily checks wiki availability for a single teamspace (called only for
   * the teamspace currently being displayed, not for every teamspace the
   * user belongs to).
   */
  getTeamspaceWikiStatus(teamspaceId: string) {
    const deferred = Q.defer();
    const url = '/teamspaces/' + teamspaceId + '/wikistatus';
    this.request('GET', url, {}, {}, {}, {}, deferred);
    return deferred.promise as Q.Promise<{ hasWiki: boolean }>;
  }

  /**
   * Fetches the user's teamspace role (ROLE_ADMIN or ROLE_USER)
   *
   * @param teamspaceId - The ID of the teamspace.
   * @param userId - The Keycloak user ID.
   * @returns ROLE_ADMIN or ROLE_USER or returns 204 status if user was not found.
   */
  getUserTeamspaceRole(teamspaceId: string | undefined, userId: string) {
    const deferred = Q.defer();
    const url = '/teamspaces/' + teamspaceId + '/users/' + userId + '/role';
    this.request('GET', url, {}, {}, {}, {}, deferred);
    return deferred.promise as Q.Promise<UserTeamspaceRoleResp>;
  }

  getTeamspacesCount(tenantId: string | undefined) {
    const deferred = Q.defer();
    const url = '/teamspaces/count/' + tenantId;
    this.request('GET', url, {}, {}, {}, {}, deferred);
    return deferred.promise as Q.Promise<Teamspace>;
  }

  invokeHREF(href: string) {
    const deferred = Q.defer();
    this.request('GET', href, {}, {}, {}, {}, deferred);
    return deferred.promise as Q.Promise<InvokeHrefResp>; // Add other type values to InvokeHrefResp type as needed
  }

  getTeamspacesByTenant(tenantId: string) {
    const deferred = Q.defer();
    const url = '/tenants/' + tenantId;
    this.request('GET', url, {}, {}, {}, {}, deferred);
    return deferred.promise;
  }

  getAllUsers() {
    const deferred = Q.defer();
    this.request('GET', '/users', {}, {}, {}, {}, deferred);
    return deferred.promise;
  }

  fetchTeamspaceMembers(teamspaceId: string) {
    const deferred = Q.defer();
    const url = `/teamspaces/${teamspaceId}/users`;
    this.request('GET', url, {}, {}, {}, {}, deferred);
    return deferred.promise as Q.Promise<FetchTeamspaceUserResponse>;
  }

  getUserTeamspaces(userUrl: string | undefined) {
    const deferred = Q.defer();
    this.request(
      'GET',
      '/teamspaceroles/search/findByUser?user=' + userUrl,
      {},
      {},
      {},
      {},
      deferred,
    );
    return deferred.promise as Q.Promise<UserTeamspaces>;
  }

  getUserTeamspaceMappings() {
    const deferred = Q.defer();
    this.request('GET', '/teamspaces/userMappings', {}, {}, {}, {}, deferred);
    return deferred.promise as Q.Promise<Record<string, string[]>>;
  }

  checkIfTeamSpaceNameExistsForUser(userUrl: string) {
    const deferred = Q.defer();
    this.request(
      'GET',
      '/teamspaces/search/findByName?name=' + userUrl,
      {},
      {},
      {},
      {},
      deferred,
    );
    return deferred.promise as Q.Promise<TeamspaceResponse>;
  }

  checkIfTeamSpaceNameIsUnique(
    tenantId: string | undefined,
    teamspaceName: string,
  ) {
    const deferred = Q.defer();
    this.request(
      'GET',
      '/teamspaces/isuniquename?tenantId=' +
        tenantId +
        '&name=' +
        teamspaceName,
      {},
      {},
      {},
      {},
      deferred,
    );
    return deferred.promise as Q.Promise<boolean>;
  }

  fetchKeycloakUsers() {
    const deferred = Q.defer();
    this.request('GET', '/users/keycloak', {}, {}, {}, {}, deferred);
    return deferred.promise as Q.Promise<FetchKeycloakUserResponse>;
  }

  inviteUsers(teamspaceID: string, nonMemberUsers: any) {
    const deferred = Q.defer();
    const url = `/users/${teamspaceID}/inviteusers`;
    this.request('POST', url, nonMemberUsers, {}, {}, {}, deferred);
    return deferred.promise;
  }

  modifyTeamspace(
    teamspaceId: string | undefined,
    associatedUserIds: string[],
  ) {
    const deferred = Q.defer();
    const url = `/teamspaces/${teamspaceId}/membership`;

    this.request(
      'PUT',
      url,
      associatedUserIds,
      { 'Content-Type': 'application/json' },
      {},
      {},
      deferred,
    );

    return deferred.promise as Q.Promise<AddUserToTeamSpaceResponse>;
  }

  retryFailedTeamspaceTask(teamspaceID: string) {
    const deferred = Q.defer();
    const url = `/teamspaces/${teamspaceID}/retry`;
    this.request('POST', url, {}, {}, {}, {}, deferred);
    return deferred.promise as Q.Promise<RetryFailedTeamspaceTaskResp>;
  }

  addUsersToTeamspace(
    teamspaceId: string,
    usernames: string[],
    async = false,
  ): Promise<TeamspaceUserUpdateTaskDto[]> {
    const deferred = Q.defer<TeamspaceUserUpdateTaskDto[]>();
    const url = `/teamspaces/${teamspaceId}/addUsers`;
    const queryParameters = { async: async.toString() };
    this.request(
      'POST',
      url,
      usernames,
      { 'Content-Type': 'application/json' },
      queryParameters,
      {},
      deferred,
    );
    return deferred.promise as unknown as Promise<TeamspaceUserUpdateTaskDto[]>;
  }

  /**
   * Update teamspace's user list by replacing the entire associatedUserIds.
   * This is used for member removal before calling addUsersToTeamspace.
   * @param {string} teamspaceId The teamspace ID.
   * @param {string[]} usernames The complete final list of usernames.
   * @returns {Promise<any>} Promise with the update response.
   */
  updateTeamspaceUsers(teamspaceId: string, usernames: string[]): Promise<any> {
    const deferred = Q.defer<any>();
    const url = `/teamspaces/${teamspaceId}/membership`;
    this.request(
      'PUT',
      url,
      usernames,
      { 'Content-Type': 'application/json' },
      {},
      {},
      deferred,
    );
    return deferred.promise as unknown as Promise<any>;
  }

  /**
   * Poll task status by taskId using Spring Data REST endpoint.
   * @param {string} taskId The UUID of the task to poll.
   * @returns {Promise<any>} Promise with the task status response.
   */
  getTeamspaceTaskStatus(taskId: string): Promise<any> {
    const deferred = Q.defer<any>();
    const url = `/teamspacetasks/${taskId}`;
    this.request('GET', url, {}, {}, {}, {}, deferred);
    return deferred.promise as unknown as Promise<any>;
  }

  revokeLicense(userId: string) {
    const deferred = Q.defer();
    const queryParameters = { userId: userId };
    this.request('DELETE', '/license', {}, {}, queryParameters, {}, deferred);
    return deferred.promise;
  }

  assignLicense(userId: string) {
    const deferred = Q.defer();
    const queryParameters = { userId: userId };
    this.request('PUT', '/license', {}, {}, queryParameters, {}, deferred);
    return deferred.promise;
  }

  getLicenses() {
    const deferred = Q.defer();
    this.request('GET', '/licenses', {}, {}, {}, {}, deferred);
    return deferred.promise;
  }

  /**
   * Save integrations for a teamspace.
   *
   * @param teamspaceId - The ID of the teamspace.
   * @param integrations - The list of integrations in JSON format.
   * @returns A promise resolving to the response of the API call.
   */
  saveIntegrations(teamspaceId: string, data: any) {
    const deferred = Q.defer();
    const url = `/teamspaces/${teamspaceId}/integrations`;
    this.request('POST', url, data, {}, {}, {}, deferred);
    return deferred.promise as Q.Promise<SaveIntegrationsResponse>;
  }

  /**
   * Retrieve the integrations JSON for a teamspace.
   *
   * @param teamspaceId - The ID of the teamspace.
   * @returns A promise resolving to the response of the API call.
   */
  getTeamspaceIntegrations(teamspaceId: string) {
    const deferred = Q.defer();
    const url = `/teamspaces/${teamspaceId}/integrations`;
    this.request('GET', url, {}, {}, {}, {}, deferred);
    return deferred.promise as Q.Promise<GetTeamspaceIntegrationsResponse>;
  }

  /**
   * Fetch the list of products that AI propagation targets.
   */
  getAiPropagationProducts(teamspaceId: string) {
    const deferred = Q.defer();
    const url = `/teamspaces/${teamspaceId}/ai-propagation-status/products`;
    this.request('GET', url, {}, {}, {}, {}, deferred);
    return deferred.promise as Q.Promise<{
      teamspaceId: string;
      products: string[];
    }>;
  }

  /**
   * Poll the AI propagation status for a teamspace.
   */
  getAiPropagationStatus(teamspaceId: string) {
    const deferred = Q.defer();
    const url = `/teamspaces/${teamspaceId}/ai-propagation-status`;
    this.request('GET', url, {}, {}, {}, {}, deferred);
    return deferred.promise as Q.Promise<AiPropagationStatusResponse>;
  }

  /**
   * Retry AI propagation for the specified failed products.
   */
  retryAiPropagation(teamspaceId: string, products: string[]) {
    const deferred = Q.defer();
    const url = `/teamspaces/${teamspaceId}/ai-propagation-status/retry`;
    this.request('POST', url, { products }, {}, {}, {}, deferred);
    return deferred.promise;
  }

  disableUserInKeycloak(username: string | undefined) {
    const deferred = Q.defer();
    this.request(
      'POST',
      '/users/disable?user=' + username + '&status=false',
      {},
      {},
      {},
      {},
      deferred,
    );
    return deferred.promise as Q.Promise<TeamspaceTaskResponse>;
  }

  getTeamspaceIconColorOptions() {
    interface IconColorOption {
      id: string;
      color: string;
      name: string;
    }
    const deferred = Q.defer();
    this.request(
      'GET',
      '/teamspaces/iconcolor/options',
      {},
      {},
      {},
      {},
      deferred,
    );
    return deferred.promise as Q.Promise<IconColorOption[]>;
  }

  updateTeamspaceIconColor(teamspaceId: string, iconColor: string) {
    const deferred = Q.defer();
    const url = `/teamspaces/${teamspaceId}/iconcolor`;
    this.request('PUT', url, { iconColor }, {}, {}, {}, deferred);
    return deferred.promise;
  }

  getFavoriteTeamspaces(loopId: string) {
    const deferred = Q.defer();
    const url = `/teamspaces/${loopId}/preferences/loops/favorites`;
    this.request('GET', url, {}, {}, {}, {}, deferred);
    return deferred.promise;
  }
  addTeamspaceToFavorites(teamspaceId: string) {
    const deferred = Q.defer();
    const url = `/teamspaces/${teamspaceId}/preferences/loops/favorites`;
    const payload = [teamspaceId];
    this.request('POST', url, payload, {}, {}, {}, deferred);
    return deferred.promise;
  }
  removeTeamspaceFromFavorites(teamspaceId: string) {
    const deferred = Q.defer();
    const url = `/teamspaces/${teamspaceId}/preferences/loops/favorites`;
    const payload = [teamspaceId];
    this.request('DELETE', url, payload, {}, {}, {}, deferred);
    return deferred.promise;
  }

  enableUserInKeycloak(username: string | undefined) {
    const deferred = Q.defer();
    this.request(
      'POST',
      '/users/disable?user=' + username + '&status=true',
      {},
      {},
      {},
      {},
      deferred,
    );
    return deferred.promise as Q.Promise<UserTeamspaces>;
  }

  getActiveAnnouncements(teamspaceId: string) {
    const deferred = Q.defer();
    const url = `/api/teamspaces/${teamspaceId}/announcements/active`;
    this.request('GET', url, {}, {}, {}, {}, deferred);
    return deferred.promise as Q.Promise<AnnouncementDto[]>;
  }

  createAnnouncement(teamspaceId: string, request: CreateAnnouncementRequest) {
    const deferred = Q.defer();
    const url = `/api/teamspaces/${teamspaceId}/announcements`;
    this.request('POST', url, request, {}, {}, {}, deferred);
    return deferred.promise as Q.Promise<AnnouncementDto>;
  }

  deleteAnnouncement(teamspaceId: string, announcementId: string) {
    const deferred = Q.defer();
    const url = `/api/teamspaces/${teamspaceId}/announcements/${announcementId}`;
    this.request('DELETE', url, {}, {}, {}, {}, deferred);
    return deferred.promise as Q.Promise<void>;
  }

  getDiagnosticArchives() {
    const deferred = Q.defer();
    this.request('GET', '/diagnostics/archives', {}, {}, {}, {}, deferred);
    return deferred.promise as Q.Promise<DiagnosticArchive[]>;
  }

  downloadDiagnosticArchive(filename: string) {
    const deferred = Q.defer();
    this.request(
      'GET',
      `/diagnostics/download/${encodeURIComponent(filename)}`,
      {},
      {},
      {},
      {},
      deferred,
      'blob',
    );
    return deferred.promise as Q.Promise<Blob>;
  }

  /**
   * Exchange the Authorization Code (from the browser popup + consent leg)
   * for the caller's offline token. The raw `token` is returned exactly once.
   */
  mintMyToken(
    authCode: string,
    redirectUri: string,
  ): Promise<OfflineTokenResponse> {
    const deferred = Q.defer<OfflineTokenResponse>();
    this.request(
      'POST',
      '/tokens/mine',
      { authCode, redirectUri },
      {},
      {},
      {},
      deferred,
    );
    return new Promise<OfflineTokenResponse>((resolve, reject) => {
      deferred.promise.then(
        (data) => resolve(data),
        (err: any) =>
          reject(new Error(err?.data?.message || 'Failed to create token')),
      );
    });
  }

  /** Revoke the caller's active offline token. Idempotent server-side. */
  revokeMyToken(): Promise<void> {
    const deferred = Q.defer<void>();
    this.request('DELETE', '/tokens/mine', {}, {}, {}, {}, deferred);
    return new Promise<void>((resolve, reject) => {
      deferred.promise.then(
        () => resolve(),
        (err: any) =>
          reject(new Error(err?.data?.message || 'Failed to delete token')),
      );
    });
  }
}

export default TenantDataService;
